import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Phone, MapPin, PackageCheck } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";
import { formatPhone } from "@/lib/phone";
import { toast } from "sonner";
import OrderDetailDrawer from "@/components/OrderDetailDrawer";

const STATUS_STYLE = {
  pending:    "bg-amber-100 text-amber-800",
  assigned:   "bg-blue-100 text-blue-800",
  in_transit: "bg-sky-100 text-sky-800",
  delivered:  "bg-emerald-100 text-emerald-800",
  failed:     "bg-rose-100 text-rose-800",
};

const DELIVER_LABEL = { same_day: "Same Day", next_day: "Next Day", otc: "OTC", pickup: "Pick Up" };

export default function PatientOrdersDialog({ patient, open, onOpenChange }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  // Order id whose POD/POF is currently being viewed. Opening a drawer while
  // this dialog is still mounted lets the reviewer switch between rows.
  const [podOrderId, setPodOrderId] = useState(null);

  useEffect(() => {
    if (!open || !patient?.id) return;
    setLoading(true);
    api.get(`/patients/${patient.id}/orders`)
      .then((r) => setOrders(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, patient?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="patient-orders-dialog">
        <DialogHeader>
          <DialogTitle>Order history — {patient?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-1 p-3 rounded-lg bg-secondary/40 text-sm">
          <div className="font-medium">{patient?.name}</div>
          <div className="text-muted-foreground flex items-center gap-1.5"><Phone className="h-3 w-3" />{formatPhone(patient?.phone)}</div>
          <div className="text-muted-foreground flex items-center gap-1.5"><MapPin className="h-3 w-3" />{patient?.address}</div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-muted-foreground text-sm">Loading orders…</div>
        ) : orders.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground text-sm" data-testid="patient-orders-empty">
            No order history yet for this patient.
          </div>
        ) : (
          <div className="rounded-xl border border-border overflow-hidden" data-testid="patient-orders-table">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Order #</th>
                  <th className="px-3 py-2 text-left font-medium">Items</th>
                  <th className="px-3 py-2 text-left font-medium">Deliver</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-right font-medium">Miles</th>
                  <th className="px-3 py-2 text-right font-medium">Price</th>
                  <th className="px-3 py-2 text-right font-medium">POD</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const hasPOD =
                    o.status === "delivered" || o.status === "failed" ||
                    o.pod_photo_id || o.pod_signature_id || o.fail_photo_id;
                  return (
                  <tr key={o.id} className="border-t border-border" data-testid={`patient-order-row-${o.id}`}>
                    <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                      {o.created_at ? fmtDateTime(o.created_at) : "—"}
                    </td>
                    <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap" data-testid={`patient-order-number-${o.id}`}>
                      {o.order_number || "—"}
                    </td>
                    <td className="px-3 py-2">
                      {(o.items || []).map((it, i) => (
                        <div key={i} className="text-xs">
                          <span className="font-medium">{it.qty}×</span> {it.name}
                        </div>
                      ))}
                    </td>
                    <td className="px-3 py-2 text-xs">{DELIVER_LABEL[o.deliver_type] || "—"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_STYLE[o.status] || "bg-secondary text-foreground"}`}>
                        {o.status?.replace("_", " ")}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-num text-xs">{o.miles != null ? o.miles.toFixed(1) : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-num text-xs">{o.price != null ? `$${Number(o.price).toFixed(2)}` : "—"}</td>
                    <td className="px-3 py-2 text-right">
                      {hasPOD ? (
                        <button
                          type="button"
                          onClick={() => setPodOrderId(o.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-primary/30 bg-primary/5 text-primary hover:bg-primary/15 transition-colors text-[11px] font-semibold"
                          title="Proof of delivery — view photos, signature, comments"
                          data-testid={`patient-order-pod-${o.id}`}
                        >
                          <PackageCheck className="h-3.5 w-3.5" />
                          POD
                        </button>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>

      {/* POD viewer — reuses OrderDetailDrawer which already renders the
          proof-of-delivery / proof-of-failure panel with photos, signature,
          and driver comments. Radix portals both the dialog and drawer so
          they stack correctly regardless of mount order. */}
      <OrderDetailDrawer
        orderId={podOrderId}
        open={!!podOrderId}
        onClose={() => setPodOrderId(null)}
      />
    </Dialog>
  );
}
